<template>
  <b-container fluid class="main-container d-flex flex-column">
    <TopBar />
    <StartModal id="start-modal" @close="startGameIfUnstarted" :gamePhase="gamePhase" />
    <FinishModal
      v-if="isGamePhaseFinished"
      id="finish-modal"
      :rounds="rounds"
      @new-game="startGame"
    />
    <div class="map-container flex-grow-1">
      <GmapMap
        ref="gameMapRef"
        map-type-id="satellite"
        :center="gameMapLocation"
        :zoom="zoomLevel"
        :options="gameMapOptions"
        class="game-map"
      >
        <GmapMarker
          v-if="isRoundPhaseGuessed && !currentRound.isSkipped"
          key="guessed-location"
          :position="currentRound.guessedLocation"
          :clickable="isRoundPhaseGuessed"
          :draggable="false"
          :icon="{url: guessIcon}"
          @click="showGuessedLocationInfo = true"
        >
          <GmapInfoWindow
            :opened="showGuessedLocationInfo"
            @closeclick="showGuessedLocationInfo = false"
          >
            Your guess: {{currentRound.guessedDistanceString()}} away<br />
            Zoomed out: {{currentRound.timesZoomedOut()}} times<br />
            <strong>Score: {{currentRound.points}}</strong>
          </GmapInfoWindow>
        </GmapMarker>
        <GmapMarker
          v-if="isRoundPhaseGuessed || isRoundPhaseGuessing"
          key="actual-location"
          :icon="{url: targetIcon}"
          :position="currentRound.location"
          :clickable="isRoundPhaseGuessed"
          :draggable="false"
          @click="showActualLocationInfo = true"
        >
          <GmapInfoWindow
            v-if="isRoundPhaseGuessed"
            :opened="showActualLocationInfo"
            @closeclick="showActualLocationInfo = false"
          >
            <strong>{{currentRound.locationName}}</strong>
          </GmapInfoWindow>
        </GmapMarker>
        <GmapPolyline
          v-if="isRoundPhaseGuessed && !currentRound.isSkipped"
          :draggable="false"
          :editable="false"
          :path="[currentRound.location, currentRound.guessedLocation]"
          :options="distanceLineOptions"
        />
      </GmapMap>
      <GmapMap
        v-if="isGamePhaseStarted"
        ref="selectMapRef"
        map-type-id="roadmap"
        @click="makeGuess"
        :center="{lat: 42.5074, lng: 0.1278}"
        :zoom="0"
        :options="selectMapOptions"
        class="select-map"
      >
        <GmapRectangle
          v-if="isRoundPhaseGuessed"
          :bounds="gameMapBounds"
          :draggable="false"
          :editable="false"
          :options="{strokeWeight: 1}"
        />
      </GmapMap>
      <Controls
        :gamePhase="gamePhase"
        :isLoading="isLoading"
        :rounds="rounds"
        :roundsPerGame="roundsPerGame"
        :roundPhase="roundPhase"
        :maxResultZoomLevel="maxResultZoomLevel"
        :zoomLevel="zoomLevel"
        @new-game="startGame"
        @next="nextRound"
        @skip="skipRound"
        @zoom-out="zoomOut"
        @about="showAbout"
        class="controls-container"
      />
      <div class="loading-spinner" v-if="isLoading">
        <b-spinner label="Loading..." variant="warning" style="width: 5rem; height: 5rem;"></b-spinner>
      </div>
    </div>
  </b-container>
</template>

<script>
import { BContainer, BSpinner } from 'bootstrap-vue'
import GeoJsonGeometriesLookup from 'geojson-geometries-lookup'
import { GAME_PHASE, LOADING_LOCATIONS, MIN_SCORED_ZOOM_LEVEL, ROUNDS_PER_GAME, ROUND_PHASE } from '../../constants/whence.js'
import { GLOBAL_LANDMASS } from '../../constants/shapes/landmass.js'
import TopBar from '../common/top_bar.vue'
import guessIcon from '../../img/whence/guess-icon.png'
import targetIcon from '../../img/whence/target-icon.png'
import GenericErrorMixin from '../../libs/generic_error_mixin'
import { GoogleMapsService } from '../../libs/maps.js'
import { Round } from '../../libs/whence/round.js'
import Controls from './controls.vue'
import FinishModal from './finish_modal.vue'
import StartModal from './start_modal.vue'

export default {
  mixins: [GenericErrorMixin],
  data: function () {
    return {
      currentRound: null,
      distanceLineOptions: {
        clickable: false,
        strokeWeight: 6,
        strokeColor: '#fdcd25'
      },
      gamePhase: GAME_PHASE.unstarted,
      geoLookup: new GeoJsonGeometriesLookup(GLOBAL_LANDMASS),
      gameMapGoogle: null,
      gameMapLocation: LOADING_LOCATIONS[Math.floor(Math.random() * LOADING_LOCATIONS.length)],
      gameMapOptions: {
        // See https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
        clickableIcons: false,
        disableDefaultUI: true,
        draggableCursor: 'auto',
        draggingCursor: 'auto',
        gestureHandling: 'none',
        keyboardShortcuts: false,
        scaleControl: true,
        animatedZoom: false
      },
      guessIcon: guessIcon,
      isLoading: false,
      mapsService: null,
      maxResultZoomLevel: 4,
      rounds: [],
      roundsPerGame: ROUNDS_PER_GAME,
      roundPhase: '',
      selectMapOptions: {
        // See https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
        clickableIcons: false,
        disableDefaultUI: true,
        draggableCursor: 'auto',
        draggingCursor: 'auto',
        gestureHandling: 'none',
        keyboardShortcuts: false,
        // Disabling equator/date line as per https://stackoverflow.com/a/18160224
        styles: [
          {
            "featureType": "administrative",
            "elementType": "geometry.fill",
            "stylers": [{ "visibility": "off" }]
          }
        ]
      },
      showActualLocationInfo: true,
      showGuessedLocationInfo: true,
      targetIcon: targetIcon,
      zoomLevel: 13
    }
  },
  mounted: async function() {
    this.$bvModal.show('start-modal');
    this.isLoading = true;
    this.gameMapGoogle = await this.$refs.gameMapRef.$mapPromise;
    this.isLoading = false;
    this.mapsService = new GoogleMapsService();
  },
  computed: {
    gameMapBounds() {
      return this.zoomLevel && this.gameMapGoogle.getBounds();
    },
    isGamePhaseFinished() {
      return this.gamePhase === GAME_PHASE.finished;
    },
    isGamePhaseStarted() {
      return this.gamePhase === GAME_PHASE.started;
    },
    isRoundPhaseGuessed() {
      return this.isGamePhaseStarted && this.roundPhase === ROUND_PHASE.guessed;
    },
    isRoundPhaseGuessing() {
      return this.isGamePhaseStarted && this.roundPhase === ROUND_PHASE.guessing;
    }
  },
  methods: {
    async finishGame() {
      this.gamePhase = GAME_PHASE.finished;
      await this.$nextTick();
      this.$bvModal.show('finish-modal');
    },
    fitBounds(locations) {
      var bounds = new google.maps.LatLngBounds();
      locations.forEach((location) => {
        bounds.extend(location)
      });
      this.gameMapGoogle.fitBounds(bounds);
      const newZoomLevel = this.gameMapGoogle.getZoom() - 1;
      if (newZoomLevel > this.maxResultZoomLevel) {
        this.zoomLevel = this.maxResultZoomLevel;
      } else {
        this.zoomLevel = newZoomLevel;
      }
    },
    makeGuess(clickEvent) {
      if (this.roundPhase !== ROUND_PHASE.guessing) {
        return;
      }

      this.currentRound.guessedLocation = clickEvent.latLng;
      this.currentRound.finalZoomLevel = this.zoomLevel;

      this.currentRound.points = this.currentRound.calculatePoints();
      this.roundPhase = ROUND_PHASE.guessed;
      this.fitBounds([this.currentRound.location, this.currentRound.guessedLocation]);
    },
    async nextRound() {
      if (this.rounds.length == this.roundsPerGame) {
        await this.finishGame();
        return;
      }

      const newLocation = this.randomLocation();
      this.isLoading = true;
      try {
        var newMaxZoomLevel;
        var locationName
        try {
          newMaxZoomLevel = await this.mapsService.getMaxZoom(newLocation);
          locationName = await this.mapsService.geocodeAdmin2(newLocation);
        } catch (error) {
          this.showGenericErrorModal();
          return;
        }

        await this.setUnanimatedMapLocation(newLocation, newMaxZoomLevel);

        this.currentRound = new Round({
          location: this.gameMapLocation,
          locationName: locationName,
          maxZoomLevel: this.zoomLevel
        });
        this.rounds.push(this.currentRound);
        this.roundPhase = ROUND_PHASE.guessing;

        await this.$nextTick();
      } finally {
        this.isLoading = false;
      }
    },
    randomLocation() {
      const precision = 10**8;
      let latLng = null;
      while (latLng == null || this.geoLookup.countContainers({type: "Point", coordinates: [latLng.lng, latLng.lat]}) == 0) {
        latLng = {
          lng: Math.floor(Math.random() * 360 * precision) / precision - 180,
          lat: Math.round(Math.acos(2 * Math.random() - 1) * 180 * precision / Math.PI) / precision - 90
        };
      }
      return latLng;
    },
    async setUnanimatedMapLocation(location, zoomLevel) {
      // No native support to disable anmiation in the maps API.
      // This workaround adapted from https://gis.stackexchange.com/a/132995
      const mapContainer = this.$refs.gameMapRef;
      const mapObject = await mapContainer.$mapPromise;
      google.maps.event.addListenerOnce(
        mapObject,
        "idle",
        function() {
          mapContainer.$el.style.visibility = '';
        }
      );
      mapContainer.$el.style.visibility = 'hidden';

      this.gameMapLocation = location;
      this.zoomLevel = zoomLevel;
      await this.$nextTick();
    },
    showAbout() {
      this.$bvModal.show('start-modal');
    },
    async skipRound() {
      this.currentRound.isSkipped = true;
      this.currentRound.points = 0;

      this.roundPhase = ROUND_PHASE.guessed;
      this.zoomLevel = 3;
    },
    async startGame() {
      this.gamePhase = GAME_PHASE.unstarted;
      this.rounds = [];
      await this.nextRound();
      this.gamePhase = GAME_PHASE.started;
    },
    async startGameIfUnstarted() {
      if (this.gamePhase == GAME_PHASE.unstarted) {
        await this.startGame();
      }
    },
    zoomOut() {
      this.zoomLevel -= 1;
      if (this.zoomLevel <= MIN_SCORED_ZOOM_LEVEL) {
        this.skipRound();
      }
    }
  },
  components: {
    'b-container': BContainer,
    Controls,
    FinishModal,
    StartModal,
    TopBar
  }
}
</script>

<style scoped>
/* Basic layout adapted from https://stackoverflow.com/a/50553414 */
.main-container {
  height: 100%;
  padding: 0;
}
.map-container {
  position: relative;
  width: 100%;
  max-width: 1600px;
  height: 100%;
  margin: auto;
}
.game-map {
  width: 100%;
  min-height: 100%;
}
.select-map {
  width: 280px;
  height: 190px;
  position: absolute;
  bottom: 0;
  right: -3px;
  margin-bottom: 20px;
  border-top: 2px white solid;
  border-left: 2px white solid;
  border-bottom: 2px white solid;
  border-radius: 4px;
}
.controls-container {
  position: absolute;
  top: 0;
}
.loading-spinner {
  position: absolute;
  top: 48%;
  left: 0;
  width: 100%;
  text-align: center;
}
</style>
